import { getSendFormsUrl, colid2, prompts, getTypeFieldIndex } from "../components/env";
import { getSupabase, supabaseSchema } from "../components/supabaseClient";
import {
  fuzzyMatchFormType,
  createFormUrls,
  extractPatientInfo,
} from "./fuzzyFormMapping";
import { fetchCurrentSchemaSettings } from "./schemaFormSettings";
import { isInGroup } from "../config/roles";
import { altPhoneFor, rowPhones } from "./smsPhone";

export const handleFormSending = async (newPatient, toast) => {
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (userId) {
    const { data: userRow } = await supabase.from("users").select("role").eq("id", userId).single();
    if (userRow?.role && isInGroup(userRow.role, 'tenantSchema')) {
      return false;
    }
  }
  console.log("New patient data:", newPatient);
  console.log("Patient type from data:", newPatient.type);

  // Use fuzzy matching to validate form type
  const patientType = newPatient[prompts[getTypeFieldIndex()][0]];
  const normalizedType = fuzzyMatchFormType(patientType);

  if (!normalizedType || (normalizedType !== "NP" && normalizedType !== "NS")) {
    console.log(
      "[FUZZY MAPPING] Invalid type, no form sent:",
      patientType,
      "->",
      normalizedType,
    );
    toast({
      title: "Success",
      description:
        "Patient added successfully. No forms were sent as this patient type doesn't require forms.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    return false;
  }

  console.log(
    "[FUZZY MAPPING] Valid form type:",
    patientType,
    "->",
    normalizedType,
  );
  const formType = normalizedType;

  // Same as AdminPage sendForms: schema_settings for this tenant, then createFormUrls
  const settings = await fetchCurrentSchemaSettings();
  const formUrlList = createFormUrls([formType], settings);
  if (formUrlList.length === 0) {
    toast({
      title: "No form URL for this practice",
      description: `Configure ${formType}_FORM_URL in admin settings for this organization (${supabaseSchema}).`,
      status: "error",
      duration: 6000,
      isClosable: true,
    });
    return false;
  }
  const formUrl = formUrlList.join(",");

  toast({
    title: "Patient Added",
    description: "Sending forms to the patient...",
    status: "info",
    duration: 3000,
    isClosable: true,
  });

  try {
    const supabase = await getSupabase();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    // Use fuzzy field mapping to extract patient information
    const extractedInfo = extractPatientInfo(newPatient);

    // Validate email format - only include if it's a valid email
    const email = extractedInfo.email || "";
    const isValidEmail =
      email && email.includes("@") && email.includes(".") && email.length > 5;

    console.log("[EMAIL VALIDATION]", {
      extractedEmail: email,
      isValidEmail: isValidEmail,
      willSendEmail: isValidEmail,
    });

    const requestData = {
      df1: {
        name: extractedInfo.name || newPatient[colid2] || "",
        date_: newPatient.date_ || "",
        appointment_time: newPatient.time_ || "",
        form_type: formType,
        form_url: formUrl,
        Email: isValidEmail ? email : "", // Only include valid emails
        phone: extractedInfo.phone
          ? extractedInfo.phone.replace(/\D/g, "")
          : "",
        // The other number on file (home phone): the backend retries there
        // when the first is a known bad number (utils/smsPhone).
        alt_phone: altPhoneFor(newPatient, extractedInfo.phone, { digitsOnly: true }),
        zoho_email: settings.ZOHO_EMAIL || "",
        zoho_password: settings.ZOHO_PASSWORD || "",
        twilio_phone: settings.TWILIO_PHONE_NUMBER || "",
        split: true, // Enable split by default for long messages
        user_id: userId,
        patient_id: newPatient.id || newPatient.uuid || null, // Include patient ID if available for updating forms_ind
        contact_methods: {
          phone: !!extractedInfo.phone,
          email: isValidEmail,
        }, // Send contact methods based on available info
      },
      fpath: "manual",
      schema: supabaseSchema, // For backend to store in correct schema
    };

    console.log(
      "[FUZZY FIELD MAPPING] Form request data with extracted info:",
      {
        originalPatient: newPatient,
        extractedInfo: extractedInfo,
        requestData: requestData,
      },
    );

    const sendFormsUrl = getSendFormsUrl();
    console.log("[DEBUG] FormUtils - Sending forms to URL:", sendFormsUrl);
    console.log(
      "[DEBUG] FormUtils - Request data:",
      JSON.stringify(requestData, null, 2),
    );
    console.log("[DEBUG] FormUtils - Form type being sent:", formType);

    const response = await fetch(sendFormsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(requestData),
    });

    console.log("[DEBUG] FormUtils - Response status:", response.status);
    console.log("[DEBUG] FormUtils - Response headers:", response.headers);

    let responseData;
    try {
      responseData = await response.json();
      console.log("[DEBUG] FormUtils - Response data:", responseData);
    } catch (jsonError) {
      console.log(
        "[DEBUG] FormUtils - Failed to parse response JSON:",
        jsonError,
      );
      console.log(
        "[DEBUG] FormUtils - Raw response text:",
        await response.text(),
      );
      throw new Error("Invalid response format from server");
    }

    if (response.ok) {
      toast({
        title: "Success",
        description: "Patient added and forms sent successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      return true;
    } else {
      throw new Error(responseData.message || "Failed to send forms");
    }
  } catch (error) {
    console.error("Error sending forms:", error);
    toast({
      title: "Error",
      description:
        "Patient added but failed to send forms. Please try sending forms manually.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    return false;
  }
};

export const handleWelcomeSending = async (newPatient, toast) => {
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (userId) {
    const { data: userRow } = await supabase.from("users").select("role").eq("id", userId).single();
    if (userRow?.role && isInGroup(userRow.role, 'tenantSchema')) {
      return false;
    }
  }
  // Extract phone using fuzzy field mapping (same as handleFormSending)
  const extractedInfo = extractPatientInfo(newPatient);
  // Cell, else home phone (utils/smsPhone rule 1); the backend picks between
  // `phone` and `home_phone` itself.
  const primaryPhone = extractedInfo.phone || rowPhones(newPatient)[0] || "";
  const phone = primaryPhone.replace(/\D/g, "");
  const homePhone = altPhoneFor(newPatient, primaryPhone, { digitsOnly: true });

  if (!phone) {
    console.log("[WELCOME] No phone number found, skipping welcome message");
    return false;
  }

  try {
    const supabase = await getSupabase();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    const settings = await fetchCurrentSchemaSettings();

    const backendUrl = getSendFormsUrl().replace(
      "/send-forms",
      "/send-welcome",
    );

    const patientName = extractedInfo.name || newPatient[colid2] || "Patient";

    console.log("[WELCOME] Sending welcome message to:", patientName, phone);

    const response = await fetch(backendUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        patient_name: patientName,
        phone: phone,
        home_phone: homePhone,
        email: extractedInfo.email || "",
        appointment_date: newPatient.date_ || "",
        appointment_time: newPatient.time_ || "",
        patient_id: newPatient.id || "",
        schema: supabaseSchema,
        twilio_phone: settings.TWILIO_PHONE_NUMBER || "",
        zoho_email: settings.ZOHO_EMAIL || "",
        zoho_password: settings.ZOHO_PASSWORD || "",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to send welcome message");
    }

    toast({
      title: "Welcome Message Sent",
      description: `Welcome message automatically sent to ${patientName}.`,
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    return true;
  } catch (error) {
    console.error("[WELCOME] Error sending welcome message:", error);
    toast({
      title: "Welcome Message Failed",
      description:
        error.message || "Failed to send welcome message to new patient.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    return false;
  }
};
